/**
 * postMessage protocol with the partner SDK.
 *
 * **Must stay in sync with** `backend/wallet-management/sdk/src/browser/protocol.ts`.
 * Bumping `HORUS_AUTH_PROTOCOL_VERSION` here without bumping the SDK
 * (or vice-versa) breaks every deployed partner integration. When the
 * shape changes, increment the version on BOTH sides in the same PR
 * and add a backward-compat branch on the SDK reader.
 */

export const HORUS_AUTH_PROTOCOL_VERSION = 1;
export const HORUS_AUTH_CHANNEL = 'horus.auth';

export type HorusAuthFlow =
  | 'google'
  | 'email_password'
  | 'email_link'
  | 'phone'
  | 'unified'
  | 'reveal';

/**
 * Methods the `unified` flow may surface. Subset of `HorusAuthFlow` —
 * excludes `unified` itself (no recursion) and `reveal` (different use
 * case). When the SDK opens `?flow=unified` it can pass
 * `&methods=google,email_password` to limit the chooser; absent →
 * all four are shown.
 */
export type HorusUnifiedMethod =
  | 'google'
  | 'email_password'
  | 'email_link'
  | 'phone';

export interface HorusAuthOpenParams {
  flow: HorusAuthFlow;
  appKey?: string;
  origin: string;
  state?: string;
  email?: string;
  phone?: string;
}

export interface HorusAuthMessageEnvelope<T = HorusAuthMessageBody> {
  channel: typeof HORUS_AUTH_CHANNEL;
  version: typeof HORUS_AUTH_PROTOCOL_VERSION;
  state?: string;
  body: T;
}

export type HorusAuthMessageBody =
  | { type: 'ready' }
  | {
      type: 'success';
      idToken: string;
      /**
       * Firebase refresh token, so the SDK can renew the ID token without
       * reopening this page.
       *
       * Optional, and withheld on purpose whenever the partner origin is not
       * on the app's registered `allowedOrigins`. An ID token dies in an
       * hour; a refresh token does not, so the two do not deserve the same
       * admission test. Absent, the SDK behaves exactly as it always has —
       * a one-hour session ending in a re-login — which is the right outcome
       * for an origin we cannot vouch for.
       */
      refreshToken?: string;
      user?: {
        uid?: string;
        email?: string;
        phone?: string;
        displayName?: string;
        photoURL?: string;
        providerId?: string;
      };
      expiresAt?: number;
    }
  | { type: 'error'; code: string; message: string }
  | { type: 'cancel' }
  | {
      /**
       * Reveal flow finished. NO key material crosses this boundary —
       * the private key stays in the iframe's DOM until the user
       * dismisses. Partners only learn whether the user actually
       * viewed the key (`viewed`) so they can update their UI / audit.
       */
      type: 'reveal_complete';
      viewed: boolean;
    };
